import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
  FetchArgs,
} from "@reduxjs/toolkit/query/react";
import { API } from "aws-amplify";
import { CognitoOidcConfig } from "../CognitoOidcConfig";
import {
  CreateUserInfo,
  GetCognitoUsersResponse,
  XlsxProcessorRequest,
} from "./types/gallus";
import { getUserSessionItem } from "..";
import Bugsnag from "@bugsnag/js";
import { FetchBaseQueryArgs } from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import { CognitoJwtPayload } from "../features/console/consoleSlice";

type ServerlessQuerySupportedMethods = "post" | "get" | "put" | "delete";

export const serverlessQuery: BaseQueryFn<
  {
    url: string;
    body: any;
    method?: ServerlessQuerySupportedMethods;
    headers?: any;
  },
  unknown,
  { reason: any },
  {},
  {}
> = async (args, api, extraOptions) => {
  let responseData: any;
  const defaultHeaders = {
    "Access-Token": getUserSessionItem("accessToken"),
    "App-Client-Id": CognitoOidcConfig.clientId,
  };
  try {
    let response;
    const headers = {
      ...defaultHeaders,
      ...args.headers,
    };
    switch (args.method ?? "post") {
      case "post":
        response = await API.post("ServerlessAPI", args.url, {
          body: args.body,
          headers,
        });
        break;
      case "get":
        response = await API.get("ServerlessAPI", args.url, {
          headers,
        });
        break;
      case "put":
        response = await API.put("ServerlessAPI", args.url, {
          body: args.body,
          headers,
        });
        break;
      case "delete":
        response = await API.del("ServerlessAPI", args.url, {
          body: args.body,
          headers,
        });
        break;
      default:
        throw new Error("Unsupported method: " + args.method);
    }
    responseData = response;
  } catch (error: any) {
    if (args.url === "/thoughtspot/token") {
      const newcall = await baseFallbackQueryWithErrorManagement(
        { url: args.url, body: args.body, method: "post" },
        api,
        extraOptions,
      );
      responseData = newcall;
    } else {
      throw new Error(error.status);
    }
  }
  return { data: responseData };
};

export const serverlessApi = createApi({
  reducerPath: "serverlessApi",
  baseQuery: serverlessQuery,
  tagTypes: ["ListCognitoUsers"],
  endpoints: (builder) => ({
    getTSAccessToken: builder.query<{ token: string }, number>({
      query: (orgId) => {
        const accessToken = getUserSessionItem("accessToken");
        const username = getUserSessionItem("userName");
        return {
          url: "/thoughtspot/token",
          body: {
            username: username,
            orgId: orgId,
            accessToken: accessToken,
            clientId: CognitoOidcConfig.clientId,
          },
        };
      },
    }),
    xlsxProcessor: builder.query({
      query: (body: XlsxProcessorRequest) => {
        return {
          url: "/xlsx/",
          body: {
            AccessToken: getUserSessionItem("accessToken"),
            ...body,
          },
        };
      },
    }),
    newUserTempPasswordResend: builder.query<void, string>({
      query: (username) => {
        return {
          url: "/new-user/temp-password",
          body: {
            username,
          },
        };
      },
    }),
    getCognitoUsers: builder.query<
      GetCognitoUsersResponse,
      {
        cognitoGroup: string;
        nextToken: string | null;
        userEmail?: string;
        miscParams?: string;
      }
    >({
      query: (args) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("group_name", args.cognitoGroup);
        if (args.nextToken) {
          urlSearchParams.set("pagination_token", args.nextToken);
        }
        if (args.userEmail) {
          urlSearchParams.set("user_details", args.userEmail);
        }
        if (args.miscParams) {
          urlSearchParams.set("misc_params", args.miscParams);
        }
        return {
          url: `/user?${urlSearchParams.toString()}`,
          body: {},
          method: "get",
        };
      },
      providesTags: ["ListCognitoUsers"],
    }),
    createCognitoUser: builder.mutation<void, CreateUserInfo>({
      query: (args) => {
        let bodyNewUser = args.preferredUsername
          ? {
              username: args.username,
              preferredUsername: args.preferredUsername,
              email: args.email,
              given_name: args.given_name,
              family_name: args.family_name,
              groups: args.groups,
            }
          : {
              username: args.username,
              email: args.email,
              given_name: args.given_name,
              family_name: args.family_name,
              groups: args.groups,
            };

        return {
          url: "/user",
          body: bodyNewUser,
          method: "put",
        };
      },
      invalidatesTags: ["ListCognitoUsers"],
    }),
    deleteCognitoUser: builder.mutation<void, string>({
      query: (cognitoGroup) => {
        return {
          url: "/user",
          body: {
            userName: cognitoGroup,
          },
          method: "delete",
        };
      },
      invalidatesTags: ["ListCognitoUsers"],
    }),
    jwtProcessor: builder.query<
      CognitoJwtPayload | null,
      { clientId: string; idToken: string }
    >({
      query: (arg) => {
        return { url: "/jwt/", body: arg };
      },
      transformResponse: (response: {
        isValid: boolean;
        payload: CognitoJwtPayload;
      }) => {
        if (response.isValid) {
          return response.payload;
        } else {
          /*           Bugsnag.notify("Invalid User Payload detected", function (event) {
            event.severity = "error";
            event.context = "Invalid User Payload detected";
          }); */
          return null;
        }
      },
    }),
  }),
});

export const {
  useGetTSAccessTokenQuery,
  useLazyGetTSAccessTokenQuery,
  useXlsxProcessorQuery,
  useLazyXlsxProcessorQuery,
  useNewUserTempPasswordResendQuery,
  useLazyNewUserTempPasswordResendQuery,
  useLazyJwtProcessorQuery,
  useGetCognitoUsersQuery,
  useLazyGetCognitoUsersQuery,
  useDeleteCognitoUserMutation,
  useCreateCognitoUserMutation,
} = serverlessApi;

const fallbackApiBaseURL: FetchBaseQueryArgs = {
  baseUrl: "https://gallus-api-v3.gallusanalytics.com",
};
const baseFallbackQuery = fetchBaseQuery(fallbackApiBaseURL);

const baseFallbackQueryWithErrorManagement: BaseQueryFn<
  string | FetchArgs,
  unknown
> = async (args, api, extraOptions) => {
  try {
    let result = await baseFallbackQuery(args, api, extraOptions);
    return result;
  } catch (error) {
    Bugsnag.notify(String(error), function (event) {
      event.severity = "error";
      event.context = "Serverless Fallback API Error";
      event.setUser(
        getUserSessionItem("userName") ?? "User not in UserSession",
      );
      event.addMetadata("API Arguments", {
        endpoint: api.endpoint,
        args: JSON.stringify(args),
      });
    });
    return {
      error: {
        reason: error,
      },
    };
  }
};

export const serverlessFallbackApi = createApi({
  reducerPath: "serverlessFallbackApi",
  baseQuery: baseFallbackQueryWithErrorManagement,
  endpoints: (builder) => ({
    getTSAccessTokenFallback: builder.query<
      { token: string },
      { orgID: Number }
    >({
      query: (args) => {
        const accessToken = getUserSessionItem("accessToken");
        const username = getUserSessionItem("userName");
        return {
          url: "/thoughtspot/token",
          method: "post",
          body: {
            username: username,
            orgId: args.orgID,
            accessToken: accessToken,
            clientId: CognitoOidcConfig.clientId,
          },
        };
      },
    }),
  }),
});

export const { useLazyGetTSAccessTokenFallbackQuery } = serverlessFallbackApi;

/*
export const thoughtSpotAccessTokenQuery: BaseQueryFn<
    number, // Args
    unknown, // Result
    { reason: any }, // Error
    {}, // DefinitionExtraOptions
    {} // Meta
> = async (arg, api, extraOptions) => {
    let accessToken = getUserSessionItem('accessToken');
    let username = getUserSessionItem("userName");
    try {
        const response = await API.post("ServerlessAPI", '/thoughtspot/token', {
            body: {
                username: username,
                orgId: arg,
                accessToken: accessToken,
                clientId: CognitoOidcConfig.clientId
            },
        });
        return { data: response.token };
    }
    catch (ex) {
        return { 
            error: {
                reason: ex
            }
        };
    }
}
*/
